/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Extent from '@arcgis/core/geometry/Extent';
import dynamic from 'next/dynamic';
import Features, {ADMIN_LANDMARK_GEOFENCE_PAGE} from 'security/Features';
import {useReduxState} from '@ion/applications/src/hooks/useReduxState';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {error} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {GenericObject} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import {contains} from '@ion/core/src/utils/StringUtil';
import {EXTENT_FACTOR} from '@ion/dls/src/maps/constants';
import {useSavedMapViews} from '@ion/dls/src/maps/hooks/useSavedMapViews';
import {useViewExtent} from '@ion/dls/src/maps/hooks/useViewExtent';
import {MapProps} from '@ion/dls/src/maps/types/types';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {BaseMapEnum, ColorModeEnum} from '@ion/dls/src/types/types';
import getMonitor from '@ion/telemetry/src/apm/monitors/PagePerformanceMonitor';
import {
  exitFindNearby,
  initializeOperationalLayers,
  resetSelectedAsset,
  SET_TRACKING_MAP_VIEW,
  updateLayers
} from '@/store/actions/tracking';
import {getTrackingExtent, storeTrackingExtent} from '@/store/helpers';
import {userSelector} from '@/store/selectors/login-selectors';
import {
  findNearbyIsActiveSelector,
  findNearbyV2IsActiveSelector,
  findNearbyV2Selector,
  mapBasemapSelector,
  mapLayerSelector,
  mapViewSelector,
  savedMapViewsSelector
} from '@/store/selectors/tracking-selectors';
import {Route} from '../../../RouteDefinitions';
import {getSaveMapViewExtent} from '../../helpers';
import useBottomSheetProps from './hooks/useBottomSheetProps';
import useLayerChooserProps from './hooks/useLayerChooserProps';
import useSetViewExtent from './hooks/useViewExtent';
import useWebMapId from './hooks/useWebMapId';
import BoundaryLayerContainer from './layers/BoundaryLayerContainer';
import FleetLayerContainer from './layers/FleetLayerContainer';
import GeofenceLayerContainer from './layers/GeofenceLayerContainer';
import LiveHistoryLayerContainer from './layers/LiveHistoryLayerContainer';
import MapContextMenuLayerContainer from './layers/MapContextMenuLayerContainer';
import OperationalLayerContainer from './layers/OperationalLayerContainer';
import FindNearbyContainer from './widgets/FindNearbyContainer';
import LiveHistoryContainer from './widgets/LiveHistoryContainer';
import MapWidgetContainer from './widgets/MapWidgetContainer';
import TimeModePanelContainer from './widgets/TimeModePanelContainer';
import SelectedAssetFocus from './SelectedAssetFocus';

const DynamicMapContainer = dynamic(() => import('@ion/dls/src/maps/MapContainer'), {
  ssr : false
});

const TrackingMapContainer = ({renderMapWidgets, onSetView, onSetMapId}: any) => {
  const dispatch                                 = useDispatch();
  const isMounted                                = useHasMounted();
  const webMapId                                 = useWebMapId();
  const getDefaultMapView                        = useSavedMapViews();
  const {onToggleClustering}                     = useLayerChooserProps();
  const {currentBasemap}                         = useBottomSheetProps();
  const {initializeViewExtent, getInitialExtent} = useViewExtent();
  const fleetLayerContainerRef                   = useRef<GenericObject>();
  const [__mapView, setMapView]                  = useReduxState(SET_TRACKING_MAP_VIEW, mapViewSelector);
  const savedMapViews                            = useSelector(savedMapViewsSelector);
  const findNearby                               = useSelector(findNearbyV2Selector);
  const basemap                                  = useSelector(mapBasemapSelector);
  const layers                                   = useSelector(mapLayerSelector);
  const user                                     = useSelector(userSelector);
  const findNearbyPanelActiveRef                 = useRef(findNearby?.isPanelActive);
  const mapviewRef                               = useRef(null);
  const [initialViewExtent, setInitialViewExtent]  = useState(null);
  const webMapIdRef                                = useRef(webMapId);
  const {getGroupAssetCountAndExtent, queryParams} = useSetViewExtent(null as any);
  const {isCurrent: isCurrentTheme}                = useThemeContext();
  const findNearbyIsActive                         = useSelector(findNearbyIsActiveSelector);
  const findNearbyV2IsActive                       = useSelector(findNearbyV2IsActiveSelector);

  // the current theme has no Find Nearby - one left open in the legacy theme
  // is closed the moment the theme switches over, the same way its own chip
  // closes it, so neither its widget nor its range is left on the map ...
  useEffect(() => {
    if (isCurrentTheme && (findNearbyIsActive || findNearbyV2IsActive)) {
      dispatch(exitFindNearby(true) as any);
    }
  }, [isCurrentTheme]);

  useEffect(() => {
    if (renderMapWidgets) {
      initializeViewExtent(true);
    }
  }, [renderMapWidgets]);

  const localStorageExtent   = useMemo(() => getTrackingExtent(), []);
  const defaultSavedMapView  = useMemo(() => getDefaultMapView(savedMapViews), [savedMapViews]);
  const savedMapViewExtent   = useMemo(() => getSaveMapViewExtent(defaultSavedMapView), [defaultSavedMapView]);
  
  /**
   * Synchronizes the map's initial view extent whenever the WebMap ID or
   * relevant parameters change.
   *
   * Logic:
   *  1. If the WebMap ID has changed, fetch the latest asset extent and update the view.
   *  2. If no new extent data is available, fall back to the saved or local storage extent.
   */
  useEffect(() => {
    let isMounted = true; // Prevent updates if component unmounts
    
    const syncInitialExtent = async() => {
      const hasWebMapChanged =
        webMapIdRef.current && webMapIdRef.current !== webMapId;
      // Skip redundant execution if nothing changed
      if (!hasWebMapChanged && !savedMapViewExtent && !localStorageExtent) return;

      try {
        // 1. Handle updated WebMap ID — fetch and set the corresponding asset extent
        if (hasWebMapChanged) {
          webMapIdRef.current   = webMapId;
          const {count, extent} = await getGroupAssetCountAndExtent(
            queryParams.params as any
          );
          if (isMounted && count && extent) {
            const expandedExtent = new Extent(extent).expand(EXTENT_FACTOR);
            setInitialViewExtent(expandedExtent as any);
            return;
          }
        }
        // 2. Fallback: derive extent from saved or local storage values
        if (isMounted) {
          const fallbackExtent = getInitialExtent({
            map           : null,
            assetsExtent  : savedMapViewExtent as any,
            mapViewExtent : localStorageExtent
              ? [{extent: localStorageExtent}]
              : null,
            expand : false
          });
          setInitialViewExtent(fallbackExtent);
          webMapIdRef.current = webMapId;
        }
      } catch (err) {
        error(err);
      }
    };
    syncInitialExtent();
    // Cleanup on unmount
    return () => {
      isMounted = false;
    };
  }, [webMapId, JSON.stringify(queryParams.params), localStorageExtent, savedMapViewExtent]);
  
  const mapViewInitialExtent = useMemo(() => initialViewExtent
    , [isMounted, initialViewExtent, savedMapViewExtent, localStorageExtent]);

  const [editGeofenceGraphic, setEditGeofenceGraphic] = useState(null);
  const [clusterColor, setClusterColor] = useState<ColorModeEnum>(
    ColorModeEnum.dark
  );
  const [isFleetAssetLayerRendered, setFleetAssetLayerRendered] = useState(false);

  const canAddGeofence = Features.has(ADMIN_LANDMARK_GEOFENCE_PAGE);

  useEffect(() => {
    findNearbyPanelActiveRef.current = findNearby?.isPanelActive;
  }, [findNearby]);

  useEffect(() => {
    const color = (basemap === BaseMapEnum.hybrid ||
      basemap === BaseMapEnum.satellite) ? ColorModeEnum.light : ColorModeEnum.dark;
    setClusterColor(color);
  }, [basemap, renderMapWidgets]);

  useEffect(() => {
    const trackingMapLayers = UserPreferences.get(Preference.trackingMapLayers);
    if (trackingMapLayers) {
      dispatch(updateLayers(trackingMapLayers, layers) as any);
    }
  }, [user]);

  const onResetSelectedAsset = useCallback(() => {
    !findNearbyPanelActiveRef.current && dispatch(resetSelectedAsset({isMapSelection: true}) as any);
  }, []);

  const setOperationalLayers = useCallback(map => {
    dispatch(initializeOperationalLayers(map) as any);
  }, []);

  const onZoomToFit = useCallback(() => {
    if (fleetLayerContainerRef.current) {
      fleetLayerContainerRef.current.zoomToFit();
    }
  }, [fleetLayerContainerRef]);

  const onMapViewChange = useCallback(view => {
    const {extent}     = view || {};
    mapviewRef.current = view;
    setMapView(view as any);
    if (extent) {
      storeTrackingExtent(extent);
    }
    // we hook into the first map view change for determining when page monitoring
    // is to be marked as completed ...
    onPageMonitorComplete();
  }, []);

  /**
   * Provides a convenience function for determining if Page Monitoring is currently
   * active. If so, and if the current Page being Monitored is Tracking 2, then Page
   * Monitoring will automatically be marked as completed.
   */
  const onPageMonitorComplete = () => {
    const monitor = getMonitor();
    if (monitor?.isRunning()) {
      if (contains(monitor?.context, Route.TRACKING)) {
        monitor.stop();
      }
    }
  };

  const mapProps: MapProps = useMemo(() => ({
    webMapId,
    onMapViewChange,
    onToggleClustering,
    storeTrackingExtent,
    setOperationalLayers,
    onResetSelectedAsset,
    basemap                : currentBasemap,
    disableZoomingDblClick : true
  }), [
    webMapId,
    currentBasemap,
    onMapViewChange,
    onToggleClustering,
    storeTrackingExtent,
    setOperationalLayers,
    onResetSelectedAsset
  ]);

  const isExtentAvailable = mapViewInitialExtent || (!savedMapViewExtent && !localStorageExtent);

  // focusing the map on a newly selected asset lives in its own component -
  // the selected asset refreshes every update cycle, and reading it here
  // re-rendered the whole map (every layer and widget) each time ...
  const selectedAssetFocus = <SelectedAssetFocus mapViewRef={mapviewRef} />;

  if (isExtentAvailable && currentBasemap) {
    return (
      <>
        {selectedAssetFocus}
        <DynamicMapContainer key={`${webMapId}`}
          enableMapAutoCluster={true}
          initialExtent={mapViewInitialExtent}
          mapProps={mapProps}
          onSetView={onSetView}>
          <FleetLayerContainer ref={fleetLayerContainerRef}
            setFleetAssetLayerRendered={setFleetAssetLayerRendered}
            isFleetAssetLayerRendered={isFleetAssetLayerRendered}
            clusterColor={clusterColor} />
          <BoundaryLayerContainer />
          <MapContextMenuLayerContainer />
          <OperationalLayerContainer />
          <LiveHistoryLayerContainer />
          <LiveHistoryContainer />
          {renderMapWidgets && isCurrentTheme && (<TimeModePanelContainer />)}
          {renderMapWidgets && !isCurrentTheme && (
            <FindNearbyContainer isFleetAssetLayerRendered={isFleetAssetLayerRendered}
              onSetMapId={onSetMapId} />
          )}
          <GeofenceLayerContainer editGeofenceGraphic={editGeofenceGraphic}
            setEditGeofenceGraphic={setEditGeofenceGraphic}
            canAddGeofence={canAddGeofence} />
          {renderMapWidgets && (<MapWidgetContainer editGeofenceGraphic={editGeofenceGraphic}
            setEditGeofenceGraphic={setEditGeofenceGraphic}
            canAddGeofence={canAddGeofence}
            onZoomToFit={onZoomToFit}
            renderMapWidgets={renderMapWidgets} />
          )}
        </DynamicMapContainer>
      </>
    );
  }
  return selectedAssetFocus;
};

export default TrackingMapContainer;
